// src/services/invoiceService.js
import { doc, getDoc, updateDoc, Timestamp, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { tenantDataService } from './tenantDataService';
import { isAdvancedPlansEnabled, writeLedgerEntry } from './compPlanService';
import { getCurrentTenantId } from './tenantDataService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

const COLLECTION_NAME = 'invoices';

class InvoiceService {
  // Get all invoices
  async getAllInvoices() {
    return await tenantDataService.getAll(COLLECTION_NAME);
  }

  // Get invoice by ID
  async getInvoiceById(id) {
    return await tenantDataService.getById(COLLECTION_NAME, id);
  }

  // Create new invoice
  async createInvoice(invoiceData) {
    const invoice = await tenantDataService.create(COLLECTION_NAME, invoiceData);
    
    // Create global invoice registry entry for fast lookup
    await this.createInvoiceRegistryEntry(invoice);
    
    // Write ledger entry if advanced compensation plans are enabled
    if (await isAdvancedPlansEnabled()) {
      await this.writeLedgerForInvoice(invoice, 'invoice_created');
    }
    
    return invoice;
  }

  // Update invoice
  async updateInvoice(id, invoiceData) {
    const oldInvoice = await this.getInvoiceById(id);
    const updatedInvoice = await tenantDataService.update(COLLECTION_NAME, id, invoiceData);
    
    // Update global invoice registry if invoice number changed
    if (oldInvoice?.invoiceNumber !== updatedInvoice.invoiceNumber) {
      await this.updateInvoiceRegistryEntry(oldInvoice, updatedInvoice);
    }
    
    // Write ledger entry if advanced compensation plans are enabled and status changed to paid
    if (await isAdvancedPlansEnabled()) {
      const statusChanged = oldInvoice?.status !== invoiceData.status;
      if (statusChanged && invoiceData.status === 'paid') {
        await this.writeLedgerForInvoice(updatedInvoice, 'payment_received');
      }
    }
    
    return updatedInvoice;
  }

  // Delete invoice
  async deleteInvoice(id) {
    const invoice = await this.getInvoiceById(id);
    
    // Remove from global registry first
    if (invoice?.invoiceNumber) {
      await this.deleteInvoiceRegistryEntry(invoice.invoiceNumber);
    }
    
    return await tenantDataService.delete(COLLECTION_NAME, id);
  }

  // Get invoices by client ID
  async getInvoicesByClient(clientId) {
    return await tenantDataService.query(COLLECTION_NAME, [
      ['clientId', '==', clientId]
    ], 'createdAt', 'desc');
  }

  // Global invoice registry methods
  async createInvoiceRegistryEntry(invoice) {
    try {
      if (!invoice?.invoiceNumber) {
        console.warn('⚠️ Skipping registry entry - no invoice number');
        return;
      }
      
      const tenantId = getCurrentTenantId();
      const registryRef = doc(db, 'global_invoice_registry', invoice.invoiceNumber);
      
      await setDoc(registryRef, {
        invoiceNumber: invoice.invoiceNumber,
        invoiceId: invoice.id,
        tenantId: tenantId,
        clientId: invoice.clientId,
        clientEmail: invoice.clientEmail || null,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      console.log('✅ Created global registry entry for:', invoice.invoiceNumber);
    } catch (error) {
      console.error('❌ Failed to create invoice registry entry:', error);
      // Don't throw - registry is optimization, not critical
    }
  }

  async updateInvoiceRegistryEntry(oldInvoice, newInvoice) {
    try {
      if (!newInvoice?.invoiceNumber) {
        console.warn('⚠️ Skipping registry update - no new invoice number');
        return;
      }
      
      // Delete old entry if invoice number changed
      if (oldInvoice?.invoiceNumber && oldInvoice.invoiceNumber !== newInvoice.invoiceNumber) {
        await this.deleteInvoiceRegistryEntry(oldInvoice.invoiceNumber);
        await this.createInvoiceRegistryEntry(newInvoice);
      } else {
        // Update existing entry
        const registryRef = doc(db, 'global_invoice_registry', newInvoice.invoiceNumber);
        await updateDoc(registryRef, {
          clientId: newInvoice.clientId,
          clientEmail: newInvoice.clientEmail || null,
          updatedAt: new Date()
        });
      }
    } catch (error) {
      console.error('❌ Failed to update invoice registry entry:', error);
    }
  }

  async deleteInvoiceRegistryEntry(invoiceNumber) {
    try {
      if (!invoiceNumber) {
        console.warn('⚠️ Skipping registry deletion - no invoice number');
        return;
      }
      
      const registryRef = doc(db, 'global_invoice_registry', invoiceNumber);
      await deleteDoc(registryRef);
      console.log('🗑️ Deleted global registry entry for:', invoiceNumber);
    } catch (error) {
      console.error('❌ Failed to delete invoice registry entry:', error);
      // Don't throw - registry is optimization, not critical
    }
  }

  // Fast lookup method using global registry
  async getInvoiceByNumber(invoiceNumber) {
    try {
      // First, check global registry for fast lookup
      const registryRef = doc(db, 'global_invoice_registry', invoiceNumber);
      const registrySnap = await getDoc(registryRef);
      
      if (registrySnap.exists()) {
        const registryData = registrySnap.data();
        console.log('🚀 Fast lookup found invoice in tenant:', registryData.tenantId);
        
        // Get the actual invoice from the tenant
        const invoiceRef = doc(db, `tenants/${registryData.tenantId}/invoices`, registryData.invoiceId);
        const invoiceSnap = await getDoc(invoiceRef);
        
        if (invoiceSnap.exists()) {
          return {
            id: invoiceSnap.id,
            tenantId: registryData.tenantId,
            ...invoiceSnap.data()
          };
        }
      }
      
      console.log('❌ Invoice not found in global registry:', invoiceNumber);
      return null;
    } catch (error) {
      console.error('💥 Error in fast invoice lookup:', error);
      throw new Error(`Failed to find invoice: ${error.message}`);
    }
  }

  // Get invoices by status
  async getInvoicesByStatus(status) {
    return await tenantDataService.query(COLLECTION_NAME, [
      ['status', '==', status]
    ], 'createdAt', 'desc');
  }

  // Get invoices by date range
  async getInvoicesByDateRange(startDate, endDate) {
    return await tenantDataService.query(COLLECTION_NAME, [
      ['date', '>=', startDate],
      ['date', '<=', endDate]
    ], 'date', 'desc');
  }

  // Get paid invoices
  async getPaidInvoices() {
    return await this.getInvoicesByStatus('paid');
  }

  // Get unpaid invoices
  async getUnpaidInvoices() {
    return await this.getInvoicesByStatus('unpaid');
  }

  // Get void invoices
  async getVoidInvoices() {
    return await this.getInvoicesByStatus('void');
  }

  // Get superseded invoices
  async getSupersededInvoices() {
    return await this.getInvoicesByStatus('superseded');
  }

  // Void an invoice
  async voidInvoice(id, reason = '') {
    const invoiceData = {
      status: 'void',
      voidedAt: new Date().toISOString(),
      voidReason: reason
    };
    return await this.updateInvoice(id, invoiceData);
  }

  // Supersede an invoice
  async supersedeInvoice(invoiceId, newInvoiceId, reason) {
    const invoiceData = {
      status: 'superseded',
      supersededAt: new Date().toISOString(),
      supersededBy: newInvoiceId,
      supersededReason: reason
    };
    return await this.updateInvoice(invoiceId, invoiceData);
  }

  // Revert a voided invoice back to its original status
  async revertVoidedInvoice(invoiceId) {
    try {
      // Get the invoice using tenant data service
      const invoice = await tenantDataService.getById(COLLECTION_NAME, invoiceId);
      
      if (!invoice) {
        throw new Error('Invoice not found');
      }
      
      if (invoice.status !== 'void') {
        throw new Error('Invoice is not voided');
      }
      
      // Determine original status based on payment information
      const originalStatus = invoice.paymentDate ? 'paid' : 'unpaid';
      
      // Update using tenant data service
      await tenantDataService.update(COLLECTION_NAME, invoiceId, {
        status: originalStatus,
        voidedAt: null,
        voidReason: null,
        revertedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('Voided invoice reverted successfully');
    } catch (error) {
      console.error('Error reverting voided invoice:', error);
      throw error;
    }
  }

  // Revert a superseded invoice back to its original status
  async revertSupersededInvoice(invoiceId) {
    try {
      // Get the invoice using tenant data service
      const invoice = await tenantDataService.getById(COLLECTION_NAME, invoiceId);
      
      if (!invoice) {
        throw new Error('Invoice not found');
      }
      
      if (invoice.status !== 'superseded') {
        throw new Error('Invoice is not superseded');
      }
      
      // Determine original status based on payment information
      const originalStatus = invoice.paymentDate ? 'paid' : 'unpaid';
      
      // Update using tenant data service
      await tenantDataService.update(COLLECTION_NAME, invoiceId, {
        status: originalStatus,
        supersededAt: null,
        supersededBy: null,
        supersededReason: null,
        revertedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('Superseded invoice reverted successfully');
    } catch (error) {
      console.error('Error reverting superseded invoice:', error);
      throw error;
    }
  }

  // Get revenue summary
  async getRevenueSummary() {
    const allInvoices = await this.getAllInvoices();
    
    // Filter out void and superseded invoices from revenue calculations
    const activeInvoices = allInvoices.filter(invoice => 
      invoice.status !== 'void' && invoice.status !== 'superseded'
    );
    
    const totalRevenue = activeInvoices
      .filter(invoice => invoice.status === 'paid')
      .reduce((sum, invoice) => sum + (invoice.total || 0), 0);
    
    const pendingRevenue = activeInvoices
      .filter(invoice => invoice.status === 'unpaid')
      .reduce((sum, invoice) => sum + (invoice.total || 0), 0);
    
    return {
      totalRevenue,
      pendingRevenue,
      totalInvoices: activeInvoices.length,
      paidInvoices: activeInvoices.filter(invoice => invoice.status === 'paid').length,
      unpaidInvoices: activeInvoices.filter(invoice => invoice.status === 'unpaid').length,
      voidInvoices: allInvoices.filter(invoice => invoice.status === 'void').length,
      supersededInvoices: allInvoices.filter(invoice => invoice.status === 'superseded').length
    };
  }

  // Subscribe to invoice changes
  subscribeToInvoices(callback, conditions = []) {
    return tenantDataService.subscribe(COLLECTION_NAME, callback, conditions);
  }

  // Unsubscribe from invoice changes
  unsubscribeFromInvoices(listenerId) {
    tenantDataService.unsubscribe(listenerId);
  }

  // Generate next invoice number
  async getNextInvoiceNumber() {
    try {
      const [allInvoices, settings] = await Promise.all([
        this.getAllInvoices(),
        tenantDataService.getAll('settings')
      ]);
      
      const clinicSettings = settings.length > 0 ? settings[0] : {};
      const startingNumber = clinicSettings.startingInvoiceNumber || 1;
    
    if (allInvoices.length === 0) {
      return `INV-${String(startingNumber).padStart(7, '0')}`;
    }
    
    // Find the highest invoice number
    const invoiceNumbers = allInvoices
      .map(invoice => invoice.invoiceNumber)
      .filter(num => num && num.includes('INV-'))
      .map(num => {
        // Extract the last numeric part from invoice numbers like INV-INV-0000001 or INV-0000001
        const matches = num.match(/(\d+)$/);
        if (matches) {
          const parsed = parseInt(matches[1]);
          return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
      })
      .filter(num => num > 0);
    
      const maxNumber = invoiceNumbers.length > 0 ? Math.max(...invoiceNumbers) : startingNumber - 1;
      return `INV-${String(maxNumber + 1).padStart(7, '0')}`;
    } catch (error) {
      console.error('❌ InvoiceService.getNextInvoiceNumber failed:', error);
      throw new Error(`Failed to generate invoice number: ${error.message}`);
    }
  }

  // Send invoice email with duplicate prevention
  async sendInvoiceEmail(invoiceId, clientData = null, tenantData = null) {
    try {
      const invoice = await this.getInvoiceById(invoiceId);
      if (!invoice) {
        throw new Error('Invoice not found');
      }

      // Check if email was sent recently (within last 30 seconds)
      const now = new Date();
      const lastEmailSent = invoice.lastEmailSent;
      if (lastEmailSent) {
        const lastSentDate = lastEmailSent.seconds ? new Date(lastEmailSent.seconds * 1000) : new Date(lastEmailSent);
        const timeDiff = now - lastSentDate;
        if (timeDiff < 30000) { // 30 seconds
          throw new Error('Email was sent recently. Please wait before sending again.');
        }
      }

      // Get client data if not provided
      let clientEmail, clientName;
      if (clientData) {
        clientEmail = clientData.email;
        clientName = clientData.name;
      } else {
        // Fallback to invoice data
        clientEmail = invoice.clientEmail;
        clientName = invoice.clientName;
      }

      if (!clientEmail) {
        throw new Error('Client email not found');
      }

      // Get tenant data if not provided
      const tenantId = tenantData?.id || getCurrentTenantId();
      if (!tenantId) {
        throw new Error('Tenant ID not found');
      }

      // Generate PDF attachment using HealeHub invoice design
      let pdfBase64 = null;
      try {
        const pdfService = await import('../services/pdfService');
        pdfBase64 = await pdfService.default.generateHealeHubInvoicePDF(
          invoice, 
          clientData, 
          tenantData
        );
        console.log('✅ Generated HealeHub-styled PDF for invoice email attachment');
      } catch (error) {
        console.warn('⚠️ Failed to generate PDF attachment:', error.message);
        // Continue without PDF - email will still send
      }

      // Call the Cloud Function with proper parameters
      const sendInvoiceEmailFn = httpsCallable(functions, 'sendInvoiceEmail');
      const response = await sendInvoiceEmailFn({
        tenantId: tenantId,
        toEmail: clientEmail,
        clientEmail: clientEmail,
        clientName: clientName,
        templateData: {
          invoiceNumber: invoice.invoiceNumber,
          total: invoice.total,
          currency: invoice.currency || 'GBP'
        },
        clinic: {
          name: tenantData?.name || 'Healthcare Clinic',
          email: tenantData?.email || '',
          address: tenantData?.address || '',
          phone: tenantData?.phone || ''
        },
        invoice: {
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          date: invoice.date,
          dueDate: invoice.dueDate,
          total: invoice.total,
          subtotal: invoice.subtotal,
          discount: invoice.discount || 0,
          tax: invoice.tax || 0,
          currency: invoice.currency || 'GBP',
          lineItems: invoice.lineItems || [],
          status: invoice.status || 'unpaid'
        },
        pdfBase64: pdfBase64
      });

      // Update invoice with email sent timestamp
      await this.updateInvoice(invoiceId, {
        lastEmailSent: new Date(),
        emailSentCount: (invoice.emailSentCount || 0) + 1
      });

      return response.data;
    } catch (error) {
      console.error('Error sending invoice email:', error);
      throw error;
    }
  };

  // Mark invoice as paid
  async markAsPaid(invoiceId) {
    return await this.updateInvoice(invoiceId, {
      status: 'paid',
      paymentDate: new Date(),
      updatedAt: new Date()
    });
  }

  // Mark invoice as unpaid
  async markAsUnpaid(invoiceId) {
    return await this.updateInvoice(invoiceId, {
      status: 'unpaid',
      paymentDate: null,
      updatedAt: new Date()
    });
  }

  // Write ledger entry for invoice events
  async writeLedgerForInvoice(invoice, eventType) {
    try {
      // Get appointment data to determine staff member
      const appointments = await tenantDataService.getAll('appointments');
      const appointment = appointments.find(apt => apt.id === invoice.appointmentId);
      
      if (!appointment || !appointment.staffId) {
        console.warn('No staff member found for invoice:', invoice.id);
        return;
      }

      // Determine amount based on event type
      let amount = 0;
      let description = '';
      
      switch (eventType) {
        case 'invoice_created':
          amount = invoice.total || 0;
          description = `Invoice ${invoice.invoiceNumber} created for ${invoice.clientName}`;
          break;
        case 'payment_received':
          amount = invoice.total || 0;
          description = `Payment received for invoice ${invoice.invoiceNumber}`;
          break;
        default:
          console.warn('Unknown event type for ledger:', eventType);
          return;
      }

      // Write ledger entry
      await writeLedgerEntry({
        staffId: appointment.staffId,
        eventType,
        eventId: invoice.id,
        appointmentId: appointment.id,
        clientId: invoice.clientId,
        amount,
        description,
        metadata: {
          invoiceNumber: invoice.invoiceNumber,
          treatmentType: appointment.type,
          appointmentDate: appointment.date,
          invoiceDate: invoice.date
        }
      });

    } catch (error) {
      console.error('Error writing ledger entry for invoice:', error);
      // Don't throw - ledger writing shouldn't break invoice operations
    }
  }
}

const invoiceService = new InvoiceService();
export { invoiceService };
export default invoiceService;
